<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { answerConfirm, confirmHost, confirmRequest } from '../../services/ui/confirmDialog'
import { vDialog } from '../../utils/keyboardNav'

const confirmButton = ref<HTMLButtonElement | null>(null)
const cancelButton = ref<HTMLButtonElement | null>(null)

// Destructive questions start on Cancel, so a stray Enter doesn't delete anything.
watch(confirmRequest, async (request) => {
  if (!request) return
  await nextTick()
  ;(request.danger ? cancelButton.value : confirmButton.value)?.focus()
})

// Capture phase, so Escape answers the dialog instead of also closing Settings beneath it.
function handleKeyDown(event: KeyboardEvent) {
  if (!confirmRequest.value || event.key !== 'Escape') return
  event.preventDefault()
  event.stopImmediatePropagation()
  answerConfirm(false)
}

onMounted(() => {
  confirmHost.mounted = true
  window.addEventListener('keydown', handleKeyDown, true)
})
onUnmounted(() => {
  confirmHost.mounted = false
  window.removeEventListener('keydown', handleKeyDown, true)
  answerConfirm(false)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="confirm-fade">
      <div v-if="confirmRequest" class="confirm-overlay" @click.self="answerConfirm(false)">
        <div v-dialog class="confirm-card" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" :aria-describedby="confirmRequest.message ? 'confirm-message' : undefined">
          <h2 id="confirm-title" class="confirm-title">{{ confirmRequest.title }}</h2>
          <p v-if="confirmRequest.message" id="confirm-message" class="confirm-message">{{ confirmRequest.message }}</p>
          <div class="confirm-actions">
            <button ref="cancelButton" type="button" class="confirm-btn" @click="answerConfirm(false)">
              {{ confirmRequest.cancelLabel || 'Cancel' }}
            </button>
            <button
              ref="confirmButton"
              type="button"
              class="confirm-btn primary"
              :class="{ danger: confirmRequest.danger }"
              @click="answerConfirm(true)"
            >
              {{ confirmRequest.confirmLabel || 'OK' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 100001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(var(--black-rgb), 0.45);
}

.confirm-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(420px, 100%);
  box-sizing: border-box;
  padding: 22px 24px 18px;
  border: 1px solid var(--surface-border);
  border-radius: 14px;
  background: var(--surface-bg);
  box-shadow: var(--surface-shadow);
  font-family: var(--ui-text);
}

.confirm-title {
  margin: 0;
  color: var(--text-bright);
  font-family: var(--ui-display);
  font-size: 18px;
  font-weight: 400;
  line-height: 1.3;
}

.confirm-message {
  margin: 0;
  color: var(--text-normal);
  font-size: 13.5px;
  line-height: 1.55;
  white-space: pre-line;
}

.confirm-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}

.confirm-btn {
  padding: 7px 14px;
  border: 1px solid var(--surface-border);
  border-radius: 7px;
  background: transparent;
  color: var(--text-normal);
  cursor: pointer;
  font: 13px var(--ui-text);
}

.confirm-btn:hover {
  background: var(--button-hover-bg);
  color: var(--text-bright);
}

.confirm-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.confirm-btn.primary {
  border-color: var(--accent);
  background: var(--accent);
  color: var(--accent-contrast);
}

.confirm-btn.primary.danger {
  border-color: var(--error);
  background: var(--error);
  color: #fff;
}

.confirm-btn.primary:hover {
  filter: brightness(1.08);
}

.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.15s ease;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}
</style>
